import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AllTransactionContext, FormValues } from './index.context'
import { useForm, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { getUserProfile } from 'src/app/store/auth'
import { USER_ROLE } from 'src/app/shared/constants/app.constants'
import { useGetClientAssigned, useGetOptions } from './index.hook'
import { isArray } from 'lodash'
import { useNavigate } from 'react-router-dom'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { defaultValues } from './index.constant'
import { QuickFilter } from '../search-result/index.context'
import dayjs from 'dayjs'
import { getLookupValues } from 'src/app/store/masterSetup'
import { useTransactionFilters } from '../TransactionFiltersContext'

const AllTransactionProvider: React.FC<PropsWithChildren> = (props) => {
  const { children } = props
  const navigate = useNavigate()
  const { role } = useSelector(getUserProfile)
  const [loading, setLoading] = useState<boolean>(false)
  const { accounts, clients, handleGetAccounts, handleGetClients } = useGetClientAssigned()
  const [gettingData, setGettingData] = useState<boolean>(false)
  const lookupValues = useSelector(getLookupValues)
  const { savedFilters, saveFilters, clearFilters, clearSearchParams } = useTransactionFilters()
  const isRestoringFilters = useRef<boolean>(false)
  const hasRestoredFiltersRef = useRef<boolean>(false)
  const [hasRestoredFilters, setHasRestoredFilters] = useState<boolean>(false)

  const options = useGetOptions({
    accounts,
    clients,
  })

  const defaultFormValues = useMemo(
    () => ({
      transactionType: options.transactionOptions[0].value,
      clientIds: options.allClientValue.value,
      accountIds: options.allAccountValue.value,
      ...defaultValues,
    }),
    [options.allAccountValue.value, options.allClientValue.value, options.transactionOptions],
  )

  // ALWAYS create form with default values (never with savedFilters)
  // We restore saved values later in useEffect
  const form = useForm<FormValues>({
    defaultValues: defaultFormValues,
    mode: 'all',
  })

  const clientIds = useWatch({
    control: form.control,
    name: 'clientIds',
  })

  const accountIds = useWatch({
    control: form.control,
    name: 'accountIds',
  })

  const transactionType =
    useWatch({
      control: form.control,
      name: 'transactionType',
    }) ?? ''

  const onSubmit = useCallback(
    async (values: FormValues) => {
      console.log('values: ', values)
      try {
        setLoading(true)

        // Save filters to context for when user navigates back
        saveFilters(values)

        const dateFormat = 'YYYY-MM-DD'
        const state = {
          ...values,
          date: values.date?.map((item) => (!!item ? dayjs(item).format(dateFormat) : null)),
          paidDate: values.paidDate?.map((item) => (!!item ? dayjs(item).format(dateFormat) : null)),
          clientIds: values.clientIds,
          accountIds: values.accountIds,
          checkStatuses:
            values.checkStatuses === null
              ? lookupValues?.CheckStatus.map((item) => item.value)
              : [
                  ...(values.checkStatuses ?? []),
                  ...(values.checkStatuses?.includes('ISSUED') ? ['OS'] : []),
                  ...(values.checkStatuses?.includes('PD') ? ['PAID'] : []),
                ],
        }
        navigate(menuUrls.searchResult, {
          state,
        })
      } catch (error) {
        console.log('ERROR: ', error)
        setLoading(false)
      }
    },
    [accounts, clients, lookupValues?.CheckStatus, navigate, saveFilters],
  )

  const onReset = useCallback(() => {
    clearFilters()
    clearSearchParams()
    
    hasRestoredFiltersRef.current = false
    setHasRestoredFilters(false)
    isRestoringFilters.current = false
    
   
    Object.keys(form.getValues()).forEach((item) => {
      if (role === USER_ROLE.BANK) {
        form.resetField(item as any)
      } else {
        if (!['clientIds'].includes(item)) {
          form.resetField(item as any)
        }
      }
    })
  }, [form, role, clearFilters, clearSearchParams])

  useEffect(() => {
    if (isRestoringFilters.current) {
      return
    }

    ; (async () => {
      if (!clientIds || clientIds?.length === 0) {
        form.setValue('clientIds', options.allClientValue.value)
      }
      if (!hasRestoredFiltersRef.current) {
        form.setValue('accountIds', options.allAccountValue.value)
      }
      form.clearErrors()
      if (clientIds === null && clients.length > 0) {
        setGettingData(true)
        await handleGetAccounts(clients.map((item) => item.id))
        setGettingData(false)
      } else if (clientIds && isArray(clientIds) && clientIds.length >= 0) {
        setGettingData(true)
        await handleGetAccounts(clientIds)
        setGettingData(false)
      }
    })()
  }, [clientIds, clients, form, handleGetAccounts, options.allAccountValue.value, options.allClientValue.value])

  // clear error on changing account
  useEffect(() => {
    // Skip if we're restoring saved filters
    if (isRestoringFilters.current) {
      return
    }

    if (!accountIds || accountIds?.length === 0) {
      if (!hasRestoredFiltersRef.current) {
        form.setValue('accountIds', options.allAccountValue.value)
      }
    }
    form.clearErrors()
  }, [accountIds, form, options.allAccountValue.value])

  useEffect(() => {
    clearSearchParams()
    ; (async () => {
      setGettingData(true)
      const res = await handleGetClients()
      if (res.length === 0) {
        setGettingData(false)
      }
    })()
  }, [clearSearchParams])

  // clear all fields (except transaction type) when changing transaction type
  useEffect(() => {
    // Skip if we're restoring saved filters
    if (isRestoringFilters.current) {
      return
    }

    Object.keys(defaultFormValues).forEach((prop: string) => {
      if (prop !== 'transactionType') {
        form.resetField(prop as any)
      }
    })
    form.setValue(
      'clientIds',
      clients.length > 0 && role === USER_ROLE.CLIENT ? clients.map((item) => item.id) : options.allClientValue.value,
    )
    form.setValue('accountIds', options.allAccountValue.value)
  }, [
    defaultFormValues,
    form,
    options.allAccountValue.value,
    options.allClientValue.value,
    transactionType,
    clients,
    role,
  ])

  // re-get reason code based on transaction type
  useEffect(() => {
    options.getReasonCodesOptions({
      checkOnly: transactionType === QuickFilter.CHECKS,
    })
  }, [transactionType])

  useEffect(() => {
    // Skip if we're restoring saved filters
    if (isRestoringFilters.current) {
      return
    }

    if (clients.length > 0 && role === USER_ROLE.CLIENT) {
      if (!hasRestoredFiltersRef.current) {
        form.setValue(
          'clientIds',
          clients.map((item) => item.id),
        )
      }
    }
  }, [clients, form, role])

  useEffect(() => {
    if (savedFilters && clients.length > 0 && !hasRestoredFiltersRef.current) {
      ; (async () => {
        isRestoringFilters.current = true

        if (savedFilters.clientIds) {
          form.setValue('clientIds', savedFilters.clientIds, { shouldDirty: true, shouldTouch: true })

          if (savedFilters.clientIds && isArray(savedFilters.clientIds) && savedFilters.clientIds.length > 0) {
            setGettingData(true)
            await handleGetAccounts(savedFilters.clientIds)
            setGettingData(false)
          }
        }

        Object.keys(savedFilters).forEach((key) => {
          const value = savedFilters[key as keyof FormValues]
          if (value !== undefined) {
            form.setValue(key as any, value, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
          }
        })

        hasRestoredFiltersRef.current = true
        setHasRestoredFilters(true)

        setTimeout(() => {
          isRestoringFilters.current = false
        }, 100)
      })()
    } else if (!savedFilters) {
      // Reset flags when there are no saved filters (fresh start)
      hasRestoredFiltersRef.current = false
      setHasRestoredFilters(false)
      isRestoringFilters.current = false
    }
  }, [savedFilters, form, clients, handleGetAccounts])

  return (
    <AllTransactionContext.Provider
      value={{
        form,
        options,
        loading,
        gettingData,
        disabled: role === USER_ROLE.CLIENT,
        hasRestoredFilters,
        onSubmit,
        onReset,
      }}
    >
      {children}
    </AllTransactionContext.Provider>
  )
}

export default AllTransactionProvider
